/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, array, bool, object, string } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'clsx';
import update from 'immutability-helper';
import Title from 'components/Title/Title';

import p from 'polyglot/polyglot';

import {
  TALENT_MAKES_OFFER,
  SEND_REQUESTS_SUCCESS,
  SEND_REQUESTS_FAILURE,
  sendRequests as sendRequestsAction,
  getJob as getJobAction,
  getUserJobs as getUserJobsAction,
} from 'actions/job';

import {
  getShortlist as getShortlistAction,
  removeTalent as removeTalentAction,
} from 'actions/shortlist';

import { createUrl } from 'utils/urlHelper';

import Button from 'components/Button/Button';
import Spinner from 'components/Spinner/Spinner';
import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';

import TalentInputs from './TalentInputs/TalentInputs';
import SelectJob from './SelectJob/SelectJob';
import JobCreate from 'components/JobCreate/JobCreate';

import styles from './send-requests.scss';

/**
 * This is the SendRequests component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 * @author Niklas Rosdahl <niklas.rosdahl@vntrs.com>
 */
export class SendRequests extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    history: object.isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    shortlist: shape({
      loading: bool,
      requests: shape({
        talents_by_skill: array,
      }),
    }).isRequired,
    getShortlist: func.isRequired,
    sendRequests: func.isRequired,
    removeTalent: func.isRequired,
    getJob: func.isRequired,
    getUserJobs: func.isRequired,
  };

  /**
   * Create SendRequests.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    const form = this.createForm();

    this.state = {
      form,
      sending: false,
      createJob: false,
    };
  }

  componentDidMount() {
    this.fetchJobs();
  }

  componentDidUpdate(prevProps, prevState) {
    const { shortlist: prevShortlist } = prevProps;
    const { form: prevForm } = prevState;

    const {
      shortlist: {
        loading,
        requests: { talent_count, talents_by_skill: groups },
      },
      auth: { token },
      getJob,
    } = this.props;
    const { form } = this.state;

    if (prevForm.job_id_or_code.value !== form.job_id_or_code.value) {
      getJob(token, form.job_id_or_code.value).then(({ payload }) => {
        const newForm = groups.reduce((acc, group) => {
          const { talents } = group;

          const formattedTalents = talents.map(talent => {
            return {
              [talent.talent_id]: update(acc[talent.talent_id], {
                fields: {
                  dates: {
                    $set: {
                      valid: true,
                      value: payload.dates,
                    },
                  },
                },
              }),
            };
          });

          return Object.assign({}, acc, ...formattedTalents);
        }, form);

        this.setState({ form: newForm });
      });
    }

    if (!loading && prevShortlist.requests.talent_count !== talent_count) {
      const form = this.createForm();
      this.setState({
        form,
      });
    }
  }

  fetchJobs = () => {
    const {
      auth: { token },
      getUserJobs,
    } = this.props;

    getUserJobs(token).then(({ payload }) => {
      const { jobs } = payload;
      const { form } = this.state;

      if (jobs.length) {
        this.setState({
          form: update(form, {
            job_id_or_code: {
              $set: {
                valid: true,
                value: jobs[0].job_id,
              },
            },
          }),
        });
      }
    });
  };

  onOpenCreateJob = () => this.setState({ createJob: true });

  onCloseCreateJob = () => this.setState({ createJob: false });

  createForm = () => {
    const {
      shortlist: {
        requests: { talents_by_skill: groups },
      },
    } = this.props;

    const fields = {
      job_id_or_code: {
        value: '',
        valid: true,
        required: true,
      },
    };

    groups.map(({ skill, talents }) =>
      talents.map(talent => {
        fields[talent.talent_id] = {
          skill,
          fields: {
            dates: {
              value: '',
              valid: true,
            },
            pricing_method: {
              value: '',
              valid: true,
            },
            compensation_amount: {
              value: '',
              valid: true,
            },
            compensation_currency: {
              value: '',
              valid: true,
            },
          },
        };
      })
    );

    return fields;
  };

  onChange = (talentId, name, value) => this.validate(talentId, name, value);

  onRemove = talentId => {
    const {
      auth: { token },
      shortlist: { requestlist_id },
      removeTalent,
    } = this.props;

    if (window.confirm(p.t('general.confirm_delete'))) {
      removeTalent(token, requestlist_id, talentId);
    }
  };

  validate = (talentId, name, value) => {
    const val = value && value.trim();

    const error = !val;

    this.setInputStatus(talentId, name, value, error);

    return !error;
  };

  setInputStatus = (talentId = null, name, value, error) => {
    if (talentId) {
      this.setState(({ form }) => ({
        form: update(form, {
          [talentId]: {
            fields: {
              [name]: {
                $set: {
                  value,
                  valid: !error,
                },
              },
            },
          },
        }),
      }));
    } else {
      this.setState(({ form }) => ({
        form: update(form, {
          [name]: {
            $set: {
              value,
              valid: !error,
            },
          },
        }),
      }));
    }
  };

  submitHandler = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { form, sending } = this.state;
    const {
      auth: { token },
      shortlist: { requestlist_id },
      history,
      getShortlist,
      sendRequests,
      removeTalent,
    } = this.props;

    if (sending) return;

    const data = {
      requests: [],
    };

    const { job_id_or_code, ...groups } = form;
    const jobIdValid = this.validate(
      null,
      'job_id_or_code',
      job_id_or_code.value
    );

    let errorCount = jobIdValid ? 0 : 1;

    Object.keys(groups).map(talentId => {
      const { fields } = groups[talentId];
      const fieldsData = {};
      const pricingMethod = fields['pricing_method'].value || null;
      const talentMakesOffer = pricingMethod === TALENT_MAKES_OFFER;

      // Validate each field.
      Object.keys(fields).map(field => {
        if (
          (field === 'compensation_amount' && talentMakesOffer) ||
          (field === 'compensation_currency' && talentMakesOffer)
        ) {
          return;
        }

        const valid = this.validate(talentId, field, fields[field].value);

        if (!valid) {
          errorCount++;
        }

        fieldsData[field] = fields[field].value;
      });

      data.requests.push({
        talent_id: talentId,
        ...fieldsData,
      });
    });

    if (errorCount > 0) return;

    this.setState({ sending: true });

    sendRequests(token, job_id_or_code.value, data).then(
      ({ type, payload }) => {
        if (type === SEND_REQUESTS_SUCCESS) {
          // Remove talents from basket
          data.requests.forEach(talent => {
            this.props.removeTalent(token, requestlist_id, talent.talent_id);
          });
        }

        // Go to inbox.
        history.push(createUrl('inbox'));

        if (type === SEND_REQUESTS_FAILURE) {
          console.log('ERROR', payload);

          this.setState({ sending: false });
        }
      }
    );
  };

  /**
   * Render SendRequests.
   *
   * @return {Object}
   */
  render() {
    const { form, sending, createJob } = this.state;

    const {
      shortlist: {
        loading,
        requests: { talents_by_skill: groups },
      },
    } = this.props;

    const invalid = !!find(form, { valid: false });

    return (
      <>
        <section className={styles.container}>
          <header className={styles.header}>
            <Title title={p.t('jobs.send_requests.title')} />

            <Button className={styles.cta} onClick={this.onOpenCreateJob}>
              <BlackTieIcon className={styles.icon} icon="plus" weight="btr" />
              CREATE PROJECT
            </Button>
          </header>

          <p className={styles.tagline}>{p.t('jobs.send_requests.tagline')}</p>

          <div className={styles.row}>
            {loading && (
              <div className={styles.spinner}>
                <Spinner medium />
              </div>
            )}

            {!loading && groups.length > 0 && (
              <form
                className={classNames(styles.form, {
                  [styles.sending]: sending,
                  [styles.invalid]: invalid,
                })}
                onSubmit={this.submitHandler}
                noValidate
                autoComplete="off"
              >
                <SelectJob
                  value={form.job_id_or_code.value}
                  valid={form.job_id_or_code.valid}
                  onChange={this.onChange}
                />

                <h3 className={styles.subTitle}>
                  {p.t('jobs.send_requests.sub_title')}
                </h3>
                <p className={styles.tagline}>
                  Talents you are ready to request as a part of your project.
                </p>

                {groups.map(({ skill, talents }) => (
                  <div key={skill} className={styles.talentCard}>
                    {talents.map(
                      talent =>
                        form[talent.talent_id] && (
                          <div key={talent.talent_id} className={styles.inputs}>
                            <TalentInputs
                              {...talent}
                              formData={form[talent.talent_id].fields}
                              onChange={this.onChange}
                              onRemove={this.onRemove}
                            />
                          </div>
                        )
                    )}
                  </div>
                ))}

                <div className={styles.footer}>
                  <Button
                    className={styles.button}
                    onClick={this.submitHandler}
                    disabled={loading}
                    small
                  >
                    {p.t('jobs.send_requests.submit_button')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>
        {createJob && (
          <JobCreate
            blockNavigation
            onClose={cb => {
              this.onCloseCreateJob();
              if (cb) this.fetchJobs();
            }}
          />
        )}
      </>
    );
  }
}

export default withRouter(
  connect(
    ({ auth, shortlist }) => ({
      auth,
      shortlist,
    }),
    {
      getShortlist: getShortlistAction,
      sendRequests: sendRequestsAction,
      removeTalent: removeTalentAction,
      getJob: getJobAction,
      getUserJobs: getUserJobsAction,
    }
  )(SendRequests)
);
